import { useQuery } from 'react-query'
import { useGetGotchiOwnerQuery, useQuestsQuery } from 'vvs-gotchi-graphql'
import { useTranslation } from 'contexts/Localization'
import { ContextApi } from 'contexts/Localization/types'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { questSymbolId } from 'views/VvsGotchi/utils/quest'
import { useGotchiAuth } from 'state/vvsgotchi/hooks/useAuth'
import { QueryKey } from 'config/constants/queryKey'
import { Quest, questFromGraphqlData, publicQuestFromGraphqlData, QuestWithConfig } from '../models'
import getQuestConfigs from '../questConfigs'

interface GotchiQuestsReturnType {
  loading: boolean
  findQuests: (questFilterFn?: (quest: QuestWithConfig) => boolean) => QuestWithConfig[]
  findQuest: (symbolOrId: string) => QuestWithConfig
  refresh: () => Promise<any>
}
const emptyGotchiQuests: Omit<GotchiQuestsReturnType, 'loading'> = {
  findQuests: () => [],
  findQuest: () => null,
  refresh: async () => undefined,
}

const useGotchiQuests = (): GotchiQuestsReturnType => {
  const { account } = useActiveWeb3React()
  const { t } = useTranslation()
  const [authValid] = useGotchiAuth()

  const { data: publicQuestsData, loading: publicQuestsLoading, refetch: refetchPublicQuests } = useQuestsQuery()
  const {
    data: ownerData,
    loading: ownerLoading,
    refetch: refetchOwner,
  } = useGetGotchiOwnerQuery({
    variables: {
      address: account,
    },
    skip: !account || !authValid,
  })

  const { data: gotchiQuests } = useQuery(
    [QueryKey.VVS_GOTCHI_QUESTS, account, ownerData, publicQuestsData],
    () => {
      const questData = ownerData?.owner
        ? ownerData.owner?.quests.map(questFromGraphqlData) ?? []
        : publicQuestsData?.quests.map(publicQuestFromGraphqlData) ?? []

      const questsWithConfig = combineQuestsWithConfig(questData, t)

      return {
        findQuests: (questFilterFn?: (quest: QuestWithConfig) => boolean) =>
          selectQuests(questsWithConfig, questFilterFn),
        findQuest: (symbolOrId: string) => selectQuest(symbolOrId, questsWithConfig),
        refresh: ownerData?.owner ? refetchOwner : refetchPublicQuests,
      }
    },
    { staleTime: Infinity, keepPreviousData: true },
  )

  return {
    loading: publicQuestsLoading || ownerLoading,
    ...(gotchiQuests || emptyGotchiQuests),
  }
}

export default useGotchiQuests

const selectQuests = (quests: QuestWithConfig[], questFilterFn?: (quest: QuestWithConfig) => boolean) => {
  return questFilterFn ? quests.filter(questFilterFn) : quests
}
const selectQuest = (symbolOrId: string, quests: QuestWithConfig[]) => {
  return quests.find((q) => questSymbolId(q.symbol) === questSymbolId(symbolOrId))
}

const combineQuestsWithConfig = (quests: Quest[], t: ContextApi['t']) => {
  const questConfigs = getQuestConfigs(t)
  return quests
    .map((quest) => {
      const questConfig = questConfigs[quest.symbol]
      return { quest, questConfig }
    })
    .filter(({ questConfig }) => questConfig)
    .map(({ quest, questConfig }) => ({
      ...quest,
      ...questConfig,
    }))
}
